/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import LinkIcon from '@atlaskit/icon/glyph/link';
import { N0, R300, N400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import UnlinkButton from '../unlink-button';

export type Props = {
	isOverlapping: boolean;
	isFiltered: boolean;
	label: string;
	onUnlink?: (analyticsEvent: UIAnalyticsEvent) => void; // optional handler, controls visibility of the unlink button
};

const getBackgroundColor = (isOverlapping: boolean) =>
	isOverlapping
		? token('color.background.danger.bold', R300)
		: token('color.background.neutral.bold', N400);

const Separator = ({ label, isOverlapping, isFiltered, onUnlink }: Props) => (
	<div
		css={containerStyles}
		data-testid="roadmap.timeline-table-kit.common.ui.dependency.separator"
	>
		<div css={separatorContainerStyles}>
			<div
				css={lineStyles}
				style={{
					border: `1px ${isFiltered ? 'dashed' : 'solid'} ${getBackgroundColor(isOverlapping)}`,
				}}
			/>
			<div
				css={iconWrapperStyles}
				style={{
					backgroundColor: getBackgroundColor(isOverlapping),
				}}
			>
				<LinkIcon size="small" label={label} primaryColor={token('color.icon.inverse', N0)} />
			</div>
			<div
				css={lineStyles}
				style={{
					border: `1px ${isFiltered ? 'dashed' : 'solid'} ${getBackgroundColor(isOverlapping)}`,
				}}
			/>
		</div>
		<div css={textContainerStyles}>{label}</div>
		{onUnlink && (
			<div css={unlinkButtonWrapperStyles}>
				<UnlinkButton onClick={onUnlink} />
			</div>
		)}
	</div>
);

export default Separator;

const containerStyles = css({
	display: 'flex',
	flexDirection: 'row',
	position: 'relative',
});

const separatorContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	marginLeft: token('space.200', '16px'),
	marginRight: token('space.100', '8px'),
	alignItems: 'center',
});

const textContainerStyles = css({
	display: 'flex',
	flexGrow: 1,
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '12px',
	lineHeight: token('space.150', '12px'),
	fontWeight: 600,
});

const iconWrapperStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: token('space.250', '20px'),
	height: token('space.250', '20px'),
	boxShadow: token('elevation.shadow.raised', '1px 2px 3px 0 rgba(9, 30, 66, 0.13)'),
	borderRadius: '50%',
});

const lineStyles = css({
	height: token('space.150', '12px'),
});

const unlinkButtonWrapperStyles = css({
	position: 'absolute',
	top: token('space.150', '12px'),
	right: 0,
});
